import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Plus, GripVertical, Clock, MessageSquare, Send, UserCheck, ChevronDown, ChevronUp, User, Shield, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLUMNS = {
  todo: { id: 'todo', title: 'To Do', color: 'bg-slate-100', borderColor: 'border-slate-300', textColor: 'text-slate-700' },
  in_progress: { id: 'in_progress', title: 'In Progress', color: 'bg-blue-50', borderColor: 'border-blue-300', textColor: 'text-blue-700' },
  done: { id: 'done', title: 'Done', color: 'bg-green-50', borderColor: 'border-green-300', textColor: 'text-green-700' }
};

// Default case tasks for reserve recovery - in standard order
const DEFAULT_CASE_TASKS = [
  { title: 'Schedule Consultation', description: 'Schedule a call with our team to discuss your case strategy', assignee: 'client', priority: 'high', order: 1 },
  { title: 'Initial Case Assessment', description: 'Admin completes initial assessment of reserve recovery viability', assignee: 'admin', priority: 'high', order: 2 },
  { title: 'Verify Identity Documents', description: 'Admin verifies client identity documents for compliance', assignee: 'admin', priority: 'high', order: 3 },
  { title: 'Review Processor Agreement', description: 'Admin reviews the merchant processor agreement for terms and conditions', assignee: 'admin', priority: 'high', order: 4 },
  { title: 'Sign Agent/Referral Agreement', description: 'Client signs the referral agreement (client first, then admin counter-signs)', assignee: 'client', priority: 'high', order: 5 },
  { title: 'Upload Processing Statement', description: 'Client uploads merchant processing statements for analysis', assignee: 'client', priority: 'high', order: 6 },
  { title: 'Submit Bank Documentation', description: 'Client uploads bank statements showing held reserves', assignee: 'client', priority: 'medium', order: 7 },
  { title: 'Contact Processor', description: 'Admin initiates formal contact with the payment processor', assignee: 'admin', priority: 'high', order: 8 },
  { title: 'Prepare Demand Letter', description: 'Admin prepares formal demand letter for reserve release', assignee: 'admin', priority: 'medium', order: 9 },
];

const CaseTaskBoard = ({ caseId, caseName, clientId, clientName, isAdmin = false, onTaskUpdate }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [expandedTasks, setExpandedTasks] = useState({});
  const [newComment, setNewComment] = useState('');
  const [activeCommentTask, setActiveCommentTask] = useState(null);
  const [newTask, setNewTask] = useState({ 
    title: '', 
    description: '', 
    status: 'todo', 
    assignee: 'client',
    priority: 'medium'
  });

  useEffect(() => {
    if (caseId) {
      loadTasks();
    }
  }, [caseId]);

  const loadTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/case-tasks/${caseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        // Sort tasks by order field
        const sortedTasks = data.sort((a, b) => (a.order || 99) - (b.order || 99));
        setTasks(sortedTasks);
      }
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const newStatus = destination.droppableId;
    const oldStatus = source.droppableId;

    setTasks(prevTasks => 
      prevTasks.map(t => t.id === draggableId ? { ...t, status: newStatus } : t)
    );

    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/case-tasks/${draggableId}/status?status=${newStatus}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Task moved to ${COLUMNS[newStatus]?.title}`);
      if (onTaskUpdate) onTaskUpdate();
    } catch (error) {
      setTasks(prevTasks => 
        prevTasks.map(t => t.id === draggableId ? { ...t, status: oldStatus } : t)
      );
      toast.error('Failed to update task status');
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    if (!newTask.title.trim()) {
      toast.error('Please enter a task title');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/case-tasks`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          case_id: caseId,
          client_id: clientId,
          title: newTask.title,
          description: newTask.description || '',
          status: newTask.status,
          assignee: newTask.assignee,
          priority: newTask.priority
        })
      });

      if (response.ok) {
        const createdTask = await response.json();
        setTasks([...tasks, createdTask]);
        toast.success('Task created successfully');
        setShowAddModal(false);
        setNewTask({ title: '', description: '', status: 'todo', assignee: 'client', priority: 'medium' });
        if (onTaskUpdate) onTaskUpdate();
      }
    } catch (error) {
      toast.error('Failed to create task');
    }
  };

  const handleAddComment = async (taskId) => {
    if (!newComment.trim()) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/case-tasks/${taskId}/comments`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ content: newComment })
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
        setNewComment('');
        setActiveCommentTask(null);
        toast.success('Comment added');
      }
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const handleReassign = async (taskId, newAssignee) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/case-tasks/${taskId}/reassign?assignee=${newAssignee}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        const updatedTask = await response.json();
        setTasks(tasks.map(t => t.id === taskId ? updatedTask : t));
        toast.success(`Task reassigned to ${newAssignee === 'admin' ? 'Admin' : 'Client'}`);
      }
    } catch (error) {
      toast.error('Failed to reassign task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!confirm('Are you sure you want to delete this task?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await fetch(`${API_URL}/api/case-tasks/${taskId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(tasks.filter(t => t.id !== taskId));
      toast.success('Task deleted');
      if (onTaskUpdate) onTaskUpdate();
    } catch (error) {
      toast.error('Failed to delete task');
    }
  };

  const toggleTaskExpand = (taskId) => {
    setExpandedTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };

  const getTasksByStatus = (status) => tasks.filter(task => task.status === status);

  const getPriorityBadge = (priority) => {
    const colors = {
      high: 'bg-red-100 text-red-700 border-red-200',
      medium: 'bg-amber-100 text-amber-700 border-amber-200',
      low: 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return colors[priority] || colors.medium;
  };

  if (loading) {
    return <div className="text-center py-8 text-slate-600">Loading tasks...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-bold text-slate-900">Case Tasks - {caseName}</h3>
          <p className="text-sm text-slate-500">
            {clientName && <span>Client: {clientName} • </span>}
            Drag tasks to update status • Click to expand for comments
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowAddModal(true)}
            className="primary-button flex items-center gap-2"
          >
            <Plus size={18} />
            Add Task
          </button>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs">
        <span className="flex items-center gap-1">
          <User size={14} className="text-blue-600" /> Client Task
        </span>
        <span className="flex items-center gap-1">
          <Shield size={14} className="text-purple-600" /> Admin Task
        </span>
      </div>

      {/* Kanban Board */}
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.values(COLUMNS).map((column) => (
            <Droppable key={column.id} droppableId={column.id}>
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                  className={`rounded-xl p-4 min-h-[400px] border-2 transition-all ${column.color} ${
                    snapshot.isDraggingOver ? 'border-purple-400 bg-purple-50' : column.borderColor
                  }`}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h4 className={`font-bold text-lg ${column.textColor}`}>
                      {column.title}
                    </h4>
                    <span className={`text-sm font-semibold px-3 py-1 rounded-full ${column.color} ${column.textColor} border ${column.borderColor}`}>
                      {getTasksByStatus(column.id).length}
                    </span>
                  </div>

                  <div className="space-y-3">
                    {getTasksByStatus(column.id).map((task, index) => (
                      <Draggable key={task.id} draggableId={task.id} index={index} isDragDisabled={!isAdmin}>
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            className={`bg-white rounded-lg shadow-sm border-2 overflow-hidden ${
                              snapshot.isDragging ? 'shadow-xl ring-2 ring-purple-400 border-purple-400' : 
                              task.assignee === 'admin' ? 'border-purple-200' : 'border-blue-200'
                            }`}
                          >
                            {/* Task Header */}
                            <div className="p-4">
                              <div className="flex items-start gap-2">
                                {isAdmin && (
                                  <div
                                    {...provided.dragHandleProps}
                                    className="text-slate-400 hover:text-purple-600 cursor-grab mt-1"
                                  >
                                    <GripVertical size={16} />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 mb-1">
                                    {task.assignee === 'admin' ? (
                                      <Shield size={14} className="text-purple-600" />
                                    ) : (
                                      <User size={14} className="text-blue-600" />
                                    )}
                                    <h5 className="font-semibold text-slate-900 text-sm">{task.title}</h5>
                                  </div>
                                  
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className={`text-xs px-2 py-0.5 rounded-full border ${getPriorityBadge(task.priority)}`}>
                                      {task.priority}
                                    </span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                      task.assignee === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                                    }`}>
                                      {task.assignee === 'admin' ? 'Admin' : 'Client'}
                                    </span>
                                  </div>

                                  {task.description && (
                                    <p className="text-xs text-slate-600 line-clamp-2">{task.description}</p>
                                  )}
                                  
                                  {/* Task Meta */}
                                  <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                                    <span className="flex items-center gap-1">
                                      <Clock size={10} />
                                      {task.updated_at ? format(new Date(task.updated_at), 'MMM d') : 'N/A'}
                                    </span>
                                    {task.comments?.length > 0 && (
                                      <span className="flex items-center gap-1">
                                        <MessageSquare size={10} />
                                        {task.comments.length}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <button
                                  onClick={() => toggleTaskExpand(task.id)}
                                  className="p-1 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded"
                                >
                                  {expandedTasks[task.id] ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </button>
                              </div>
                            </div>

                            {/* Expanded Section */}
                            {expandedTasks[task.id] && (
                              <div className="border-t border-slate-200 bg-slate-50 p-3 space-y-3">
                                {/* Comments */}
                                <div>
                                  <h6 className="text-xs font-semibold text-slate-700 mb-2 flex items-center gap-1">
                                    <MessageSquare size={12} />
                                    Comments ({task.comments?.length || 0})
                                  </h6>
                                  
                                  {task.comments?.length > 0 && (
                                    <div className="space-y-2 mb-2 max-h-24 overflow-y-auto">
                                      {task.comments.map((comment, idx) => (
                                        <div key={idx} className="bg-white rounded p-2 text-xs border border-slate-200">
                                          <div className="flex justify-between items-start">
                                            <span className="font-medium text-slate-800">{comment.author_name}</span>
                                            <span className="text-slate-400">
                                              {format(new Date(comment.created_at), 'MMM d')}
                                            </span>
                                          </div>
                                          <p className="text-slate-600 mt-1">{comment.content}</p>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {/* Add Comment */}
                                  <div className="flex gap-2">
                                    <input
                                      type="text"
                                      value={activeCommentTask === task.id ? newComment : ''}
                                      onChange={(e) => {
                                        setActiveCommentTask(task.id);
                                        setNewComment(e.target.value);
                                      }}
                                      onFocus={() => setActiveCommentTask(task.id)}
                                      placeholder="Add comment..."
                                      className="flex-1 px-2 py-1.5 text-xs border border-slate-200 rounded focus:ring-1 focus:ring-purple-500 outline-none"
                                    />
                                    <button
                                      onClick={() => handleAddComment(task.id)}
                                      className="px-2 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700"
                                    >
                                      <Send size={12} />
                                    </button>
                                  </div>
                                </div>

                                {/* Actions */}
                                {isAdmin && (
                                  <div className="flex gap-2 pt-2 border-t border-slate-200">
                                    <button
                                      onClick={() => handleReassign(task.id, task.assignee === 'admin' ? 'client' : 'admin')}
                                      className="flex-1 py-1.5 text-xs border border-slate-300 text-slate-700 rounded hover:bg-slate-100 flex items-center justify-center gap-1"
                                    >
                                      <UserCheck size={12} />
                                      Reassign to {task.assignee === 'admin' ? 'Client' : 'Admin'}
                                    </button>
                                    <button
                                      onClick={() => handleDeleteTask(task.id)}
                                      className="px-3 py-1.5 text-xs border border-red-300 text-red-600 rounded hover:bg-red-50"
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    
                    {getTasksByStatus(column.id).length === 0 && (
                      <div className="text-center py-6 text-slate-400 text-xs">
                        <p>No tasks</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Droppable>
          ))}
        </div>
      </DragDropContext>

      {/* Add Task Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="p-6 border-b border-slate-200">
              <h3 className="text-xl font-bold text-slate-900">Add Task for {caseName}</h3>
            </div>
            
            <form onSubmit={handleAddTask} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Task Title *</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  placeholder="What needs to be done?"
                  required
                />
              </div>

              {/* Quick Templates */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Quick Templates</label>
                <div className="flex flex-wrap gap-2">
                  {DEFAULT_CASE_TASKS.slice(0, 6).map((template, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => setNewTask({ 
                        ...newTask, 
                        title: template.title, 
                        description: template.description,
                        assignee: template.assignee,
                        priority: template.priority
                      })}
                      className="text-xs px-2 py-1 bg-slate-100 text-slate-700 rounded hover:bg-purple-100 hover:text-purple-700"
                    >
                      {template.title}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                  className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none resize-none"
                  rows={2}
                  placeholder="Add details..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Assign To</label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setNewTask({ ...newTask, assignee: 'client' })}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border-2 flex items-center justify-center gap-2 ${
                        newTask.assignee === 'client'
                          ? 'border-blue-500 bg-blue-50 text-blue-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      <User size={14} /> Client
                    </button>
                    <button
                      type="button"
                      onClick={() => setNewTask({ ...newTask, assignee: 'admin' })}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border-2 flex items-center justify-center gap-2 ${
                        newTask.assignee === 'admin'
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : 'border-slate-200 text-slate-600 hover:border-slate-300'
                      }`}
                    >
                      <Shield size={14} /> Admin
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Priority</label>
                  <select
                    value={newTask.priority}
                    onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                    className="w-full px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 outline-none"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Initial Status</label>
                <div className="flex gap-2">
                  {Object.values(COLUMNS).map((col) => (
                    <button
                      key={col.id}
                      type="button"
                      onClick={() => setNewTask({ ...newTask, status: col.id })}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium border-2 transition-all ${
                        newTask.status === col.id
                          ? 'border-purple-500 bg-purple-50 text-purple-700'
                          : `${col.color} ${col.textColor} border-transparent hover:border-slate-300`
                      }`}
                    >
                      {col.title}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="flex-1 px-4 py-3 border border-slate-300 rounded-lg text-slate-700 hover:bg-slate-50 font-medium"
                >
                  Cancel
                </button>
                <button type="submit" className="flex-1 primary-button py-3">
                  Create Task
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseTaskBoard;
